import React, { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Hexagon, Send, Mic, Square, Loader2, ArrowLeft, RefreshCw, Volume2, VolumeX } from "lucide-react";
import Orb from "@/components/Orb";
import { useVoice } from "@/hooks/useVoice";
import { useAuth } from "@/context/AuthContext";
import api, { API } from "@/lib/api";

export default function Awaken() {
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [orbState, setOrbState] = useState("idle");
  const [generating, setGenerating] = useState(false);
  const [booted, setBooted] = useState(false);
  const [voiceOn, setVoiceOn] = useState(true);
  const scrollRef = useRef(null);
  const { recording, transcribing, startRecording, stopRecording, speak, stopSpeaking } = useVoice();

  useEffect(() => { if (!loading && !user) navigate("/auth"); }, [loading, user, navigate]);

  const boot = useCallback(async () => {
    try {
      const prof = await api.get("/system/profile");
      if (prof.data.unlocked) { navigate("/system"); return; }
      const { data } = await api.get("/awakening/messages");
      const clean = data.messages.map((m) => ({ role: m.role, content: m.content }));
      setMessages(clean);
      if (clean.length === 0) sendMessage("__start__", true);
    } catch { /* */ } finally { setBooted(true); }
  }, [navigate]);

  useEffect(() => { if (user) boot(); }, [user, boot]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, streaming]);

  const doGenerate = async () => {
    setGenerating(true);
    setOrbState("thinking");
    try {
      await api.post("/awakening/generate");
      navigate("/system");
    } catch {
      setGenerating(false);
      setOrbState("idle");
    }
  };

  const sendMessage = async (text, silent = false) => {
    const content = (text ?? input).trim();
    if ((!content && !silent) || streaming) return;
    if (!silent) {
      setInput("");
      setMessages((m) => [...m, { role: "user", content }]);
    }
    setMessages((m) => [...m, { role: "assistant", content: "" }]);
    setStreaming(true);
    setOrbState("thinking");

    let acc = "";
    let ready = false;
    try {
      const token = localStorage.getItem("archon_token");
      const res = await fetch(`${API}/awakening/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ session_id: "awaken", message: content || "Begin my awakening.", mode: "personal" }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop();
        for (const part of parts) {
          const line = part.trim();
          if (!line.startsWith("data:")) continue;
          const payload = JSON.parse(line.slice(5).trim());
          if (payload.delta) {
            acc += payload.delta;
            setOrbState("speaking");
            const shown = acc.replace("[[ASSESSMENT_COMPLETE]]", "").trim();
            setMessages((m) => {
              const copy = [...m];
              copy[copy.length - 1] = { role: "assistant", content: shown };
              return copy;
            });
          }
          if (payload.ready) ready = true;
        }
      }
    } catch {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", content: "⚠️ Signal lost. Try again." };
        return copy;
      });
    } finally {
      setStreaming(false);
      setOrbState("idle");
      const spoken = acc.replace("[[ASSESSMENT_COMPLETE]]", "").trim();
      if (voiceOn && spoken) {
        setOrbState("speaking");
        speak(spoken, () => setOrbState("idle"));
      }
      if (ready) doGenerate();
    }
  };

  const handleMic = async () => {
    if (recording) {
      setOrbState("thinking");
      const t = await stopRecording();
      setOrbState("idle");
      if (t) sendMessage(t);
    } else {
      stopSpeaking();
      setOrbState("listening");
      await startRecording();
    }
  };

  const resetAwakening = async () => {
    await api.post("/awakening/reset");
    setMessages([]);
    sendMessage("__start__", true);
  };

  if (generating) {
    return (
      <div className="h-screen grid-bg flex flex-col items-center justify-center text-center px-6">
        <Orb state="thinking" size={190} />
        <p className="mono-label mt-10">CALIBRATING BUILD</p>
        <h2 className="font-display text-3xl font-bold mt-3">The System is forging your character…</h2>
        <p className="text-slate-400 mt-2">Analyzing your stats, class and hidden potential.</p>
        <Loader2 className="animate-spin text-cyan-400 mt-8" />
      </div>
    );
  }

  return (
    <div className="h-screen w-full flex flex-col bg-[#050505] grid-bg overflow-hidden">
      <header className="flex items-center justify-between px-5 py-4 border-b border-blue-900/30">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate("/")} className="text-slate-400 hover:text-cyan-300" data-testid="back-btn"><ArrowLeft size={18} /></button>
          <div className="flex items-center gap-2">
            <Hexagon className="text-cyan-400" size={18} />
            <div>
              <p className="font-display font-semibold leading-tight">The Awakening</p>
              <p className="mono-label">{streaming ? "ARCHON IS SPEAKING…" : "INTERVIEW IN PROGRESS"}</p>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3">
        <button data-testid="awaken-voice-toggle" onClick={() => { setVoiceOn((v) => !v); stopSpeaking(); }}
          className={`p-2 rounded-full border transition-all ${voiceOn ? "border-cyan-400/50 text-cyan-300 bg-cyan-400/10" : "border-blue-900/40 text-slate-500"}`}
          title="ARCHON's voice">
          {voiceOn ? <Volume2 size={16} /> : <VolumeX size={16} />}
        </button>
        <button data-testid="reset-awakening-btn" onClick={resetAwakening} className="mono-label flex items-center gap-1.5 border border-blue-900/40 px-3 py-1.5 rounded-full hover:bg-blue-500/10 transition-all">
          <RefreshCw size={13} /> Restart
        </button>
        </div>
      </header>

      <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 sm:px-8 py-6">
        <div className="max-w-2xl mx-auto flex flex-col items-center">
          <Orb state={orbState} size={110} />
          <div className="w-full mt-8 space-y-5" data-testid="awaken-messages">
            {messages.map((m, i) => (
              <div key={i} className={`flex gap-3 rise ${m.role === "user" ? "justify-end" : "justify-start"}`} data-testid={`awaken-msg-${m.role}`}>
                {m.role === "assistant" && (
                  <div className="w-9 h-9 rounded-full shrink-0 grid place-items-center bg-gradient-to-br from-cyan-400/30 to-blue-600/30 border border-cyan-400/40">
                    <Hexagon size={15} className="text-cyan-300" />
                  </div>
                )}
                <div className={`max-w-[82%] rounded-2xl px-4 py-3 whitespace-pre-wrap leading-relaxed ${m.role === "user" ? "bg-blue-600 text-white rounded-br-sm" : "glass text-slate-100 rounded-bl-sm"}`}>
                  {m.content || (streaming && i === messages.length - 1
                    ? <span className="inline-flex gap-1 items-center"><span className="dot" /><span className="dot" /><span className="dot" /></span>
                    : "")}
                </div>
              </div>
            ))}
          </div>
          {booted && messages.length === 0 && !streaming && (
            <p className="text-slate-500 mt-10">Establishing connection with ARCHON…</p>
          )}
        </div>
      </div>

      <div className="px-4 sm:px-8 py-5 border-t border-blue-900/30 bg-[#0A0E17]/60 backdrop-blur">
        <div className="max-w-2xl mx-auto flex items-end gap-3">
          <div className="flex-1 flex items-end bg-slate-950/60 border border-blue-900/50 focus-within:border-cyan-400 rounded-2xl px-4 py-2 transition-all">
            <textarea
              data-testid="awaken-input" rows={1} value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
              placeholder={recording ? "Listening…" : transcribing ? "Transcribing…" : "Answer ARCHON…"}
              disabled={recording || transcribing || streaming}
              className="flex-1 bg-transparent outline-none resize-none text-slate-50 placeholder-slate-500 py-1.5 max-h-32"
            />
          </div>
          <button data-testid="awaken-mic-btn" onClick={handleMic} disabled={transcribing || streaming}
            className={`shrink-0 w-12 h-12 rounded-full grid place-items-center transition-all duration-300 ${recording ? "bg-red-500 shadow-[0_0_24px_rgba(239,68,68,0.6)] animate-pulse" : "border border-cyan-400/50 text-cyan-300 hover:bg-cyan-400/10"}`}>
            {transcribing ? <Loader2 size={20} className="animate-spin" /> : recording ? <Square size={18} /> : <Mic size={20} />}
          </button>
          <button data-testid="awaken-send-btn" onClick={() => sendMessage()} disabled={streaming || !input.trim()}
            className="shrink-0 w-12 h-12 rounded-full grid place-items-center bg-blue-600 hover:bg-blue-500 disabled:opacity-40 text-white transition-all hover:shadow-[0_0_22px_rgba(59,130,246,0.5)]">
            <Send size={18} />
          </button>
        </div>
      </div>
    </div>
  );
}
